import { inject } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivateFn,
    Router,
    RouterStateSnapshot,
    UrlTree,
} from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable } from 'rxjs';

import { detectPreferredLanguage, isSupportedLanguage } from '@core/i18n/language';
import { readStoredLanguage } from '@core/i18n/language-storage';

/**
 * Matches a language-tag-shaped URL prefix: a two-letter code with optional subtags (`fr`, `pt-BR`,
 * `zh-Hans`). Deliberately **not** three letters — ISO 639-3 codes never appear in web URLs, while
 * three-letter slugs do, and treating `/foo/bar` as a language would drop the `foo` segment.
 */
const LANGUAGE_TAG: RegExp = /^[a-z]{2}(-[a-z0-9]{2,8})*$/i;

/**
 * Guards the `:lang` route segment. The visitor's saved preference (set via the switcher, stored in
 * `localStorage` + cookie) always wins: any URL whose language prefix differs from it is redirected
 * to the saved language, keeping the rest of the path. When there is no saved preference, the URL
 * language is used, or {@link unsupportedLanguageRedirect} decides where an unsupported prefix goes.
 * Activation waits for the translations to load.
 */
export const languageGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
): Observable<boolean> | UrlTree => {
    const router: Router = inject(Router);
    const translate: TranslateService = inject(TranslateService);
    const document: Document = inject(DOCUMENT);

    const urlLang: string | null = route.paramMap.get('lang');
    const stored: string | null = readStoredLanguage();

    if (isSupportedLanguage(stored) && stored !== urlLang) {
        return router.parseUrl(replaceLanguageSegment(state.url, stored));
    }

    if (!isSupportedLanguage(urlLang)) {
        return router.parseUrl(unsupportedLanguageRedirect(state.url, detectPreferredLanguage()));
    }

    document.documentElement.lang = urlLang;

    return translate.use(urlLang).pipe(map((): boolean => true));
};

/**
 * Where to send a URL whose first segment is not a supported language. Two different mistakes hide
 * behind that condition, and collapsing both onto `/{language}` is what produced soft 404s — every
 * wrong URL answered 200 with the home page.
 *
 * - A language-shaped prefix (`/fr`, `/fr/about`) is a real language we do not publish → swap it for
 *   `language` and keep the rest of the path, so the visitor lands on the page they asked for.
 * - Anything else is a link that forgot the language prefix (`/impressum`) → prefix it, so the CMS
 *   gets to resolve it as a slug. If there is no such article the dynamic page renders a real 404,
 *   and a path too deep to be a slug falls through to the catch-all 404 route.
 *
 * Exported for unit testing — the redirect rules it encodes apply to every route in the app.
 */
export function unsupportedLanguageRedirect(url: string, language: string): string {
    const firstSegment: string = url.split(/[?#]/)[0].split('/').filter(Boolean)[0] ?? '';

    return LANGUAGE_TAG.test(firstSegment)
        ? replaceLanguageSegment(url, language)
        : prefixLanguageSegment(url, language);
}

/**
 * Replaces the leading language segment of a URL, preserving the rest of the path/query/fragment.
 * Exported for unit testing — see {@link unsupportedLanguageRedirect}.
 */
export function replaceLanguageSegment(url: string, language: string): string {
    return withUrlParts(url, (segments: Array<string>): Array<string> => {
        segments[0] = language;

        return segments;
    });
}

/**
 * Inserts `language` as the leading segment of a URL, preserving the rest of the path/query/fragment.
 * Exported for unit testing — see {@link unsupportedLanguageRedirect}.
 */
export function prefixLanguageSegment(url: string, language: string): string {
    return withUrlParts(url, (segments: Array<string>): Array<string> => [language, ...segments]);
}

/**
 * Splits a router URL into path segments, query and fragment, hands the segments to `transform` and
 * reassembles the result. Keeps the query/fragment handling — including a `?` that belongs to the
 * fragment rather than the query — in one place for both segment rewrites above.
 */
function withUrlParts(url: string, transform: (segments: Array<string>) => Array<string>): string {
    const hashIndex: number = url.indexOf('#');
    const hash: string = hashIndex >= 0 ? url.slice(hashIndex) : '';
    const [path, query] = (hashIndex >= 0 ? url.slice(0, hashIndex) : url).split('?');

    const segments: Array<string> = transform(path.split('/').filter(Boolean));

    return `/${segments.join('/')}${query ? `?${query}` : ''}${hash}`;
}
